import { useEffect, useMemo, useState } from 'react';

import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { BringgDashboardSDK } from '@bringg/dashboard-sdk';

import { useStores } from 'bringg-web/recipes';

function useCompanies(): Bringg.Company[] {
	const {
		usersStore: { canAccessCompanies }
	} = useStores();
	const dashboardSdk: BringgDashboardSDK = getRootEnv().dashboardSdk.sdk;

	const [companies, setCompanies] = useState<Bringg.Company[]>([]);

	useEffect(() => {
		if (!dashboardSdk || !canAccessCompanies) {
			setCompanies([] as Bringg.Company[]);
			return;
		}

		async function fetchCompanies() {
			try {
				const { companies: allCompanies } = await dashboardSdk.companies.getAll({});
				setCompanies(allCompanies);
			} catch (error) {
				console.error('[useCompanies] - failed to fetch companies', error);
				setCompanies([]);
			}
		}

		void fetchCompanies();
	}, [dashboardSdk]);

	return companies;
}

export function useCompaniesForDriverForm(): {
	companies: Bringg.Company[];
	currentUserCompanyId: number | undefined;
} {
	const {
		usersStore: { currentUser }
	} = useStores();
	const allCompanies = useCompanies();

	return useMemo(() => {
		const isRestrictedDispatcher = !currentUser.admin && !!currentUser.company_id;

		// Admin: all companies | Dispatcher without company: all | Restricted dispatcher: only their company
		const companies = isRestrictedDispatcher
			? allCompanies.filter(({ id }) => id === currentUser.company_id)
			: allCompanies;

		return {
			companies,
			currentUserCompanyId: isRestrictedDispatcher ? currentUser.company_id : undefined
		};
	}, [allCompanies, currentUser.admin, currentUser.company_id]);
}

export default useCompanies;
